import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, FormSelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import SearchSelect, { type SearchSelectItem } from '@/components/UI/SearchSelect';
import {
    hydrateTradeLogicDraftFromBackend,
    type LoadPublishedTradeLogicDraftInput,
    useLoadPublishedTradeLogicDraft,
    useTradeLogicDefinitions,
    useTradeLogicDefinitionVersions,
    type TradeLogicDefinitionSummary
} from '@/features/tradeLogicAuthoring/api';
import { buildTradeLogicEditorPath, resolveStrategyIdFromPathname } from '@/features/tradeLogicAuthoring/tradeLogicNavigation';
import TradeLogicRuleTree from './TradeLogicRuleTree';

interface TradeLogicAttachmentCardProps {
    kind: 'entry' | 'exit';
    canEdit: boolean;
    definitionId: number | null;
    versionId: number | null;
    version: number | null;
    /** Version id currently persisted on the strategy; drives unsaved-change detection. */
    liveVersionId?: number | null;
    /** Version number currently persisted on the strategy, for the "live on strategy" line. */
    liveVersion?: number | null;
    /** False while creating a strategy, when there is nothing to be live against yet. */
    isPersistedStrategy?: boolean;
    className?: string;
    onChange: (next: { definitionId: number | null; version: number | null; versionId: number | null }) => Promise<void> | void;
}

const TradeLogicAttachmentCard = ({
    kind,
    canEdit,
    definitionId,
    versionId,
    version,
    liveVersionId = null,
    liveVersion = null,
    isPersistedStrategy = false,
    className = '',
    onChange
}: TradeLogicAttachmentCardProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [isSubmittingPickerChange, setIsSubmittingPickerChange] = useState(false);

    const definitionsQuery = useTradeLogicDefinitions({
        enabled: true,
        isActiveOnly: true,
        kind,
        scope: kind === 'entry' ? 'asset' : 'trade'
    });

    const selectedDefinition = useMemo<TradeLogicDefinitionSummary | null>(
        () => definitionsQuery.definitions.find((definition) => definition.definitionId === definitionId) ?? null,
        [definitionsQuery.definitions, definitionId]
    );

    const returnTo = `${location.pathname}${location.search}`;
    const isStrategyEditor = location.pathname.startsWith('/user/tradingStrategy/');
    const currentStrategyId = resolveStrategyIdFromPathname(location.pathname);

    const isStrategyOwnedByCurrentStrategy =
        selectedDefinition !== null &&
        currentStrategyId !== undefined &&
        selectedDefinition.ownerType === 'strategy' &&
        selectedDefinition.ownerStrategyId === currentStrategyId;

    const pickerDefinitions = useMemo(
        () =>
            isStrategyEditor
                ? definitionsQuery.definitions.filter(
                      (definition) =>
                          definition.ownerType === 'library' ||
                          currentStrategyId === null ||
                          definition.ownerStrategyId === currentStrategyId
                  )
                : definitionsQuery.definitions,
        [currentStrategyId, definitionsQuery.definitions, isStrategyEditor]
    );

    const reviseRulePath =
        selectedDefinition && version !== null
            ? isStrategyEditor
                ? isStrategyOwnedByCurrentStrategy
                    ? buildTradeLogicEditorPath(kind, selectedDefinition.definitionId, returnTo, {
                          version
                      })
                    : buildTradeLogicEditorPath(kind, 'new', returnTo, {
                          copyFromDefinitionId: selectedDefinition.definitionId,
                          copyFromVersion: version
                      })
                : buildTradeLogicEditorPath(kind, selectedDefinition.definitionId, returnTo, {
                      version
                  })
            : selectedDefinition
              ? buildTradeLogicEditorPath(kind, selectedDefinition.definitionId, returnTo)
              : null;

    const createRulePath = buildTradeLogicEditorPath(kind, 'new', returnTo);
    const hasPendingAttachmentChange = isPersistedStrategy && versionId !== liveVersionId;

    const attachedRuleSummaryMode: LoadPublishedTradeLogicDraftInput['mode'] =
        isStrategyEditor && !isStrategyOwnedByCurrentStrategy ? 'fork' : 'revise';

    const statusBadge = hasPendingAttachmentChange
        ? { label: 'Unsaved change', variant: 'warning' as const }
        : isPersistedStrategy
          ? liveVersionId !== null
              ? { label: 'Live on strategy', variant: 'success' as const }
              : null
          : versionId !== null
            ? { label: 'Will attach on create', variant: 'info' as const }
            : null;

    const attachmentModeMessage = hasPendingAttachmentChange
        ? 'Selected version differs from what is live. It goes live when you save the strategy.'
        : selectedDefinition && version !== null
          ? isStrategyOwnedByCurrentStrategy
              ? `Revising starts from saved v${version} and publishes a new revision on this strategy-owned rule set.`
              : `Revising starts from saved v${version} and creates a strategy-owned fork.`
          : `Choose the ${kind} rules this strategy runs.`;

    const handlePickerChange = async (next: { definitionId: number | null; version: number | null; versionId: number | null }) => {
        setIsSubmittingPickerChange(true);

        try {
            await Promise.resolve(onChange(next));
            setIsPickerOpen(false);
        } finally {
            setIsSubmittingPickerChange(false);
        }
    };

    return (
        <Card className={className}>
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wide text-zinc-400">
                            {kind === 'entry' ? 'Entry rules' : 'Exit rules'}
                        </h4>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                            {selectedDefinition ? (
                                <>
                                    <span className="text-base font-medium text-zinc-100">{selectedDefinition.name}</span>
                                    <Badge variant={version !== null ? 'dark' : 'warning'}>
                                        {version !== null ? `v${version}` : 'Select version'}
                                    </Badge>
                                    <Badge variant="dark">
                                        {selectedDefinition.ownerType === 'strategy' ? 'Strategy-owned' : 'Shared library'}
                                    </Badge>
                                    {statusBadge && <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>}
                                </>
                            ) : (
                                <span className="text-sm text-zinc-500">No {kind} rules attached</span>
                            )}
                        </div>
                        {hasPendingAttachmentChange && liveVersion !== null && (
                            <div className="mt-1 text-xs text-zinc-500">Live on strategy: v{liveVersion}</div>
                        )}
                        {attachmentModeMessage && <div className="mt-1 text-xs text-zinc-500">{attachmentModeMessage}</div>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {canEdit && (
                            <>
                                <Button size="sm" variant="outline-dark" onClick={() => setIsPickerOpen((prev) => !prev)}>
                                    {isPickerOpen ? 'Cancel' : selectedDefinition ? 'Change' : 'Choose rules'}
                                </Button>
                                <Button size="sm" variant="outline-dark" onClick={() => navigate(createRulePath)}>
                                    New blank rules
                                </Button>
                            </>
                        )}
                        {selectedDefinition && reviseRulePath && (
                            <Button size="sm" variant="secondary" onClick={() => navigate(reviseRulePath)}>
                                {isStrategyEditor
                                    ? isStrategyOwnedByCurrentStrategy
                                        ? 'Revise rules'
                                        : 'Create strategy copy'
                                    : 'Edit rules'}
                            </Button>
                        )}
                    </div>
                </div>
                {selectedDefinition && (
                    <AttachedRuleSummary definitionId={selectedDefinition.definitionId} mode={attachedRuleSummaryMode} version={version} />
                )}
                {isPickerOpen && (
                    <RulePicker
                        applyActionLabel="Select version"
                        currentVersion={version}
                        definitions={pickerDefinitions}
                        disableClear={isPersistedStrategy && liveVersionId !== null}
                        isApplying={isSubmittingPickerChange}
                        isLoadingDefinitions={definitionsQuery.isLoading}
                        key={`${definitionId ?? 'none'}:${versionId ?? 'none'}:${version ?? 'none'}`}
                        selectedDefinitionId={definitionId}
                        selectedVersionId={versionId}
                        onChange={handlePickerChange}
                        onClear={() => handlePickerChange({ definitionId: null, version: null, versionId: null })}
                    />
                )}
            </div>
        </Card>
    );
};

const AttachedRuleSummary = ({
    definitionId,
    version,
    mode
}: {
    definitionId: number;
    version: number | null;
    mode: LoadPublishedTradeLogicDraftInput['mode'];
}) => {
    const draftQuery = useLoadPublishedTradeLogicDraft({
        input: { definitionId, ...(version !== null ? { version } : {}), ...(mode ? { mode } : {}) }
    });

    const draft = useMemo(
        () => (draftQuery.data?.draft ? hydrateTradeLogicDraftFromBackend(draftQuery.data.draft) : null),
        [draftQuery.data]
    );

    if (draftQuery.isLoading) {
        return <div className="text-xs text-zinc-500">Loading rules…</div>;
    }

    if (draftQuery.isError || !draft?.rootNode) {
        return <div className="text-xs text-zinc-500">Rules unavailable for preview.</div>;
    }

    return <TradeLogicRuleTree rootNode={draft.rootNode} />;
};

const RulePicker = ({
    applyActionLabel,
    disableClear,
    definitions,
    isApplying,
    isLoadingDefinitions,
    currentVersion,
    selectedDefinitionId,
    selectedVersionId,
    onChange,
    onClear
}: {
    applyActionLabel: string;
    disableClear: boolean;
    definitions: TradeLogicDefinitionSummary[];
    isApplying: boolean;
    isLoadingDefinitions: boolean;
    currentVersion: number | null;
    selectedDefinitionId: number | null;
    selectedVersionId: number | null;
    onChange: (next: { definitionId: number | null; version: number | null; versionId: number | null }) => Promise<void> | void;
    onClear: () => Promise<void> | void;
}) => {
    const [pendingDefinitionId, setPendingDefinitionId] = useState<number | null>(selectedDefinitionId);
    const [pendingVersionId, setPendingVersionId] = useState<number | null>(selectedVersionId);
    const [pendingVersion, setPendingVersion] = useState<number | null>(currentVersion);
    const versionsQuery = useTradeLogicDefinitionVersions({ definitionId: pendingDefinitionId, enabled: pendingDefinitionId !== null });
    const { versions } = versionsQuery;

    const definitionOptions = useMemo<SearchSelectItem[]>(
        () =>
            definitions
                .filter((definition) => definition.latestCompiledVersion !== null)
                .map((definition) => ({
                    label: definition.name,
                    meta: `Latest v${definition.latestCompiledVersion ?? '—'}`,
                    name: definition.name,
                    value: definition.definitionId
                })),
        [definitions]
    );

    const versionOptions = useMemo(
        () =>
            versions.map((version) => ({
                value: String(version.versionId),
                label: `v${version.version}`
            })),
        [versions]
    );

    const resolvedVersionId = pendingVersionId ?? versions[0]?.versionId ?? null;
    const resolvedVersion = pendingVersion ?? versions[0]?.version ?? null;
    const canAttach = pendingDefinitionId !== null && resolvedVersionId !== null && resolvedVersion !== null;

    if (isLoadingDefinitions) {
        return <DataLoadingSpinner />;
    }

    return (
        <div className="space-y-3 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <SearchSelect
                    items={definitionOptions}
                    label="Rule set"
                    mode="single"
                    placeholder="Select rules"
                    value={pendingDefinitionId}
                    onChange={(value) => {
                        setPendingDefinitionId(value);
                        setPendingVersionId(null);
                        setPendingVersion(null);
                    }}
                />
                <FormSelect
                    disabled={pendingDefinitionId === null || versionsQuery.isLoading || versionOptions.length === 0}
                    helpText="Attach an exact saved version to this strategy."
                    label="Version"
                    options={versionOptions}
                    value={resolvedVersionId !== null ? String(resolvedVersionId) : ''}
                    onChange={(event) => {
                        const versionId = event.target.value === '' ? null : Number(event.target.value);
                        const matched = versions.find((versionSummary) => versionSummary.versionId === versionId) ?? null;

                        setPendingVersionId(versionId);
                        setPendingVersion(matched?.version ?? null);
                    }}
                />
            </div>
            {pendingDefinitionId !== null && versionsQuery.isLoading && (
                <div className="text-xs text-zinc-500">Loading saved versions…</div>
            )}
            {pendingDefinitionId !== null && !versionsQuery.isLoading && versionOptions.length === 0 && (
                <div className="rounded-md border border-amber-900/60 bg-amber-950/30 p-3 text-xs text-amber-100">
                    No compiled versions are available for this rule set yet.
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    disabled={!canAttach}
                    isLoading={isApplying}
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                        void onChange({
                            definitionId: pendingDefinitionId,
                            versionId: resolvedVersionId,
                            version: resolvedVersion
                        });
                    }}
                >
                    {applyActionLabel}
                </Button>
                <Button
                    disabled={disableClear || isApplying}
                    size="sm"
                    variant="outline-dark"
                    onClick={() => {
                        void onClear();
                    }}
                >
                    Detach rules
                </Button>
            </div>
        </div>
    );
};

export default TradeLogicAttachmentCard;
